// importNotices.ts — durable "background import finished" markers (2026-10-28).
//
// A multi-page upload returns 202 and OCRs in the background queue for minutes.
// The only completion signal used to be OcrTray's toast, which requires the
// tray to have observed the job in-flight (a page remount silences it forever);
// historically it was also cut short ~1.5s later by the tray's drain page
// reload (2026-11-03: the drain now invalidates the query cache instead).
// These markers persist in localStorage (survives reloads and new tabs), are
// scoped to the firm client active at upload time, and are promoted to a
// dismissible notice once the file's OCR settles. Storage model:
// TokenPopup's try/catch helpers; evaluation is pure like ocrDrain.ts.
import { buildFileStorageHighlightUrl } from './ocrHighlight';

export const IMPORT_NOTICE_STORAGE_KEY = 'importNoticeMarkers';
export const IMPORT_NOTICE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type ImportNoticeOutcome = 'success' | 'failed' | 'unclear' | 'type_mismatch';

export interface ImportNotice {
  outcome: ImportNoticeOutcome;
  /** Statements linked to the file at promotion time (bs_linked_count). */
  count: number;
  /** '/bank-statements' for statement imports, '/file-storage?highlight=<id>' otherwise. */
  linkUrl: string;
  /** 'invoice' when linkUrl points at the invoice's own AR/AP record — the
   *  card switches to "invoice imported → View invoice" wording (Nov-4 item 2). */
  linkKind?: 'invoice';
  filename: string;
  promotedAt: number;
}

export interface ImportMarker {
  fileId: string;
  filename: string;
  /** localStorage.activeClient?.id snapshot at write time — markers only
   * evaluate while the same client is active; others stay dormant (the list
   * call is client-scoped, so a foreign client's rows are invisible here). */
  clientId: string | null;
  channel: string;
  createdAt: number;
  status: 'pending' | 'noticed';
  notice?: ImportNotice;
}

/** The subset of GET /file-storage row fields evaluateMarkerRow needs. */
export interface FileListRowLike {
  id: string | number;
  filename?: string | null;
  original_name?: string | null;
  ocr_status?: string | null;
  category?: string | null;
  bs_linked_count?: number | string | null;
  /** Present when the file promoted an invoice record — routes the banner to
   *  /ar or /ap instead of the generic File Storage highlight (Nov-4 item 2). */
  invoice_id?: string | number | null;
  invoice_direction?: string | null;
}

export type MarkerVerdict =
  | { kind: 'wait' }
  | { kind: 'expire' }
  | { kind: 'promote'; notice: Omit<ImportNotice, 'promotedAt'> };

// ── storage ────────────────────────────────────────────────────────────────

export function readImportMarkers(): ImportMarker[] {
  try {
    const raw = localStorage.getItem(IMPORT_NOTICE_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(m => m && typeof m.fileId === 'string') : [];
  } catch { return []; }
}

export function writeImportMarkers(markers: ImportMarker[]): void {
  try {
    localStorage.setItem(IMPORT_NOTICE_STORAGE_KEY, JSON.stringify(markers));
  } catch { /* quota/unavailable — notices are best-effort, never fatal */ }
}

export function pruneImportMarkers(now: number = Date.now()): void {
  const kept = readImportMarkers().filter(m => now - m.createdAt < IMPORT_NOTICE_TTL_MS);
  writeImportMarkers(kept);
}

/** activeClient key the marker is scoped to (null = firm's own workspace). */
export function activeClientKey(): string | null {
  try {
    const c = JSON.parse(localStorage.getItem('activeClient') || 'null');
    return c?.id ?? null;
  } catch { return null; }
}

export function markersForClient(clientId: string | null): ImportMarker[] {
  return readImportMarkers().filter(m => (m.clientId ?? null) === (clientId ?? null));
}

// ── lifecycle ───────────────────────────────────────────────────────────────

/** Stage a marker at the 202 hand-off. Dedupes by fileId (re-upload of the
 *  same record re-arms it); TTL-prunes first so the store stays bounded. */
export function stageImportMarker(input: { fileId: string; filename: string; channel: string }): void {
  pruneImportMarkers();
  const markers = readImportMarkers().filter(m => m.fileId !== input.fileId);
  markers.push({
    fileId: input.fileId,
    filename: input.filename,
    clientId: activeClientKey(),
    channel: input.channel,
    createdAt: Date.now(),
    status: 'pending',
  });
  writeImportMarkers(markers);
}

/** Attach the terminal notice (idempotent no-op when the marker is gone). */
export function promoteImportMarker(fileId: string, notice: Omit<ImportNotice, 'promotedAt'>): void {
  const markers = readImportMarkers();
  const m = markers.find(x => x.fileId === fileId);
  if (!m) return;
  m.status = 'noticed';
  m.notice = { ...notice, promotedAt: Date.now() };
  writeImportMarkers(markers);
}

/** Remove a marker regardless of status — covers "dismissed while pending". */
export function dismissImportMarker(fileId: string): void {
  writeImportMarkers(readImportMarkers().filter(m => m.fileId !== fileId));
}

// ── pure evaluation ─────────────────────────────────────────────────────────

const TERMINAL_STATUSES = ['completed', 'failed', 'unclear', 'skipped', 'error', 'type_mismatch'];

/**
 * Decide what a file-list row says about a pending marker. `undefined` means
 * the row is absent from the client-scoped list — the row always exists before
 * the marker is written (upload returns the id first), so absence = deleted.
 * Unknown/empty statuses wait rather than falsely expire (bounded by the TTL).
 */
export function evaluateMarkerRow(
  row: FileListRowLike | undefined,
  fallbackFilename = '',
  channel = '',
): MarkerVerdict {
  if (!row) return { kind: 'expire' };
  const status = String(row.ocr_status || '').trim();
  if (!TERMINAL_STATUSES.includes(status)) return { kind: 'wait' };

  const filename = String(row.original_name || row.filename || fallbackFilename || 'file');
  const linkUrl = buildFileStorageHighlightUrl([String(row.id)]);
  if (status === 'completed') {
    const count = Number(row.bs_linked_count || 0);
    // Owner decision 2026-10-28: multi-statement imports point at the
    // Bank Statements list (GET /bank-statements has no file filter).
    if (count > 0) {
      return { kind: 'promote', notice: { outcome: 'success', count, linkUrl: '/bank-statements', filename } };
    }
    // Nov-4 item 2 (invoice discoverability): the row already carries the
    // promoted invoice record — route to the invoice's own AR/AP page.
    const invoiceId = row.invoice_id != null ? String(row.invoice_id) : '';
    if (invoiceId) {
      let direction = String(row.invoice_direction || '').toLowerCase();
      if (direction !== 'outgoing' && direction !== 'incoming') {
        direction = channel === 'sales_invoice' ? 'outgoing'
          : channel === 'purchase_invoice' ? 'incoming' : '';
      }
      if (direction === 'outgoing' || direction === 'incoming') {
        const invoiceLink = `/${direction === 'outgoing' ? 'ar' : 'ap'}?highlight=${invoiceId}`;
        return { kind: 'promote', notice: { outcome: 'success', count: 0, linkUrl: invoiceLink, linkKind: 'invoice', filename } };
      }
    }
    return { kind: 'promote', notice: { outcome: 'success', count: 0, linkUrl, filename } };
  }
  if (status === 'type_mismatch') {
    return { kind: 'promote', notice: { outcome: 'type_mismatch', count: 0, linkUrl, filename } };
  }
  return { kind: 'promote', notice: { outcome: status === 'failed' ? 'failed' : 'unclear', count: 0, linkUrl, filename } };
}
